/**
 * Lab Order Modal — Panel-First Ordering
 *
 * Shows the recommended lab panel for the patient's therapy category.
 * Single-click ordering sends the panel ID directly to the lab API.
 * Includes collection method selection and address for at-home phlebotomy.
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  FlaskConical,
  Sparkles,
  CheckCircle2,
  Loader2,
  MapPin,
  Building2,
  Home,
  DollarSign,
  Beaker,
  ShieldCheck,
} from 'lucide-react'
import { FHIRPatient } from '@/services/fhirPatientsService'
import type { PatientAddress, TherapyLabPanel } from '@/services/labService'
import { toast } from '@/hooks/use-toast'

interface LabOrderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patient: FHIRPatient | null
  therapyPanel: TherapyLabPanel | null
  therapyName?: string
  onSubmit?: (orderData: LabOrderFormData) => Promise<void>
}

export interface LabOrderFormData {
  patientId: string
  panelId: string
  collectionMethod: 'walk_in' | 'at_home_phlebotomy'
  patientAddress?: PatientAddress
  fasting: boolean
  notes: string
}

const EMPTY_ADDRESS: PatientAddress = {
  firstLine: '',
  city: '',
  state: '',
  zip: '',
}

const LabOrderModal: React.FC<LabOrderModalProps> = ({
  open,
  onOpenChange,
  patient,
  therapyPanel,
  therapyName,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [notes, setNotes] = useState('')
  const [collectionMethod, setCollectionMethod] = useState<'walk_in' | 'at_home_phlebotomy'>('walk_in')
  const [address, setAddress] = useState<PatientAddress>(EMPTY_ADDRESS)

  React.useEffect(() => {
    if (open) {
      setNotes('')
      setCollectionMethod('walk_in')
      setAddress(EMPTY_ADDRESS)
    }
  }, [open])

  const handleAddressChange = (field: keyof PatientAddress, value: string) => {
    setAddress(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!patient) {
      toast({ title: 'Error', description: 'No patient selected', variant: 'destructive' })
      return
    }

    if (!therapyPanel) {
      toast({ title: 'Error', description: 'No lab panel configured for this therapy', variant: 'destructive' })
      return
    }

    if (collectionMethod === 'at_home_phlebotomy' && (!address.firstLine || !address.city || !address.state || !address.zip)) {
      toast({ title: 'Error', description: 'Please fill in the patient address for at-home phlebotomy', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      const orderData: LabOrderFormData = {
        patientId: patient.id,
        panelId: therapyPanel.panelIds[0],
        collectionMethod,
        patientAddress: collectionMethod === 'at_home_phlebotomy' ? address : undefined,
        fasting: therapyPanel.fastingRequired,
        notes,
      }

      if (onSubmit) {
        await onSubmit(orderData)
      }

      onOpenChange(false)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create lab order'
      toast({ title: 'Error', description: message, variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!therapyPanel) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[hsl(var(--ih-bg))] border-[hsl(var(--ih-border))]">
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--ih-text))] flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <FlaskConical className="h-4 w-4 text-purple-400" />
            </div>
            Order Lab Panel
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--ih-text-muted))]">
            {patient && (
              <span>
                For <span className="text-[hsl(var(--ih-text))] font-medium">{patient.first_name} {patient.last_name}</span>
                {therapyName && (
                  <> &middot; <span className="text-brand-teal">{therapyName}</span></>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Panel Info Card */}
        <div className="rounded-xl border border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-brand-teal/5 p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-[hsl(var(--ih-text))] font-semibold text-base">{therapyPanel.name}</h3>
              <p className="text-[hsl(var(--ih-text-muted))] text-xs mt-0.5">Comprehensive panel via Quest Diagnostics</p>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
              Recommended
            </Badge>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2 text-[hsl(var(--ih-text-secondary))]">
              <Beaker className="h-4 w-4 text-purple-400 shrink-0" />
              <div>
                <p className="text-xs text-[hsl(var(--ih-text-muted))]">Markers</p>
                <p className="text-sm font-medium">{therapyPanel.markerCount}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[hsl(var(--ih-text-secondary))]">
              <DollarSign className="h-4 w-4 text-emerald-400 shrink-0" />
              <div>
                <p className="text-xs text-[hsl(var(--ih-text-muted))]">Price</p>
                <p className="text-sm font-medium">${therapyPanel.price.toFixed(2)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[hsl(var(--ih-text-secondary))]">
              <ShieldCheck className="h-4 w-4 text-brand-teal shrink-0" />
              <div>
                <p className="text-xs text-[hsl(var(--ih-text-muted))]">Fasting</p>
                <p className="text-sm font-medium">{therapyPanel.fastingRequired ? 'Required' : 'Not required'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-2 bg-brand-teal/10 border border-brand-teal/20 rounded-lg">
            <Sparkles className="h-4 w-4 text-brand-teal shrink-0" />
            <span className="text-xs text-brand-teal/80">
              Panel selected by IntelliHealth based on therapy requirements
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Collection Method */}
          <div className="space-y-2">
            <Label className="text-[hsl(var(--ih-text-secondary))]">Collection Method</Label>
            <RadioGroup
              value={collectionMethod}
              onValueChange={(v) => setCollectionMethod(v as 'walk_in' | 'at_home_phlebotomy')}
              className="grid grid-cols-2 gap-3"
            >
              <Label
                htmlFor="method-walk-in"
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  collectionMethod === 'walk_in'
                    ? 'bg-[hsl(var(--ih-bg-elevated))] border-purple-500/30'
                    : 'bg-[hsl(var(--ih-bg-elevated))]/20 border-[hsl(var(--ih-border))]/50'
                }`}
              >
                <RadioGroupItem value="walk_in" id="method-walk-in" />
                <div>
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-[hsl(var(--ih-text-muted))]" />
                    <span className="text-sm text-[hsl(var(--ih-text))] font-medium">Walk-in</span>
                  </div>
                  <span className="text-xs text-[hsl(var(--ih-text-muted))]">Quest PSC location</span>
                </div>
              </Label>
              <Label
                htmlFor="method-at-home"
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  collectionMethod === 'at_home_phlebotomy'
                    ? 'bg-[hsl(var(--ih-bg-elevated))] border-purple-500/30'
                    : 'bg-[hsl(var(--ih-bg-elevated))]/20 border-[hsl(var(--ih-border))]/50'
                }`}
              >
                <RadioGroupItem value="at_home_phlebotomy" id="method-at-home" />
                <div>
                  <div className="flex items-center gap-1.5">
                    <Home className="h-3.5 w-3.5 text-[hsl(var(--ih-text-muted))]" />
                    <span className="text-sm text-[hsl(var(--ih-text))] font-medium">At-home</span>
                  </div>
                  <span className="text-xs text-[hsl(var(--ih-text-muted))]">Phlebotomy service</span>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Patient Address (at-home only) */}
          {collectionMethod === 'at_home_phlebotomy' && (
            <div className="space-y-3 p-3 rounded-lg border border-[hsl(var(--ih-border))]/50 bg-[hsl(var(--ih-bg-elevated))]/30">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[hsl(var(--ih-text-muted))]" />
                <Label className="text-[hsl(var(--ih-text-secondary))] text-sm font-medium">Patient Address</Label>
              </div>
              <div className="space-y-2">
                <Input
                  placeholder="Street address"
                  value={address.firstLine}
                  onChange={(e) => handleAddressChange('firstLine', e.target.value)}
                  className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] text-sm"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="City"
                    value={address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] text-sm"
                  />
                  <Input
                    placeholder="State"
                    value={address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] text-sm"
                    maxLength={2}
                  />
                  <Input
                    placeholder="ZIP"
                    value={address.zip}
                    onChange={(e) => handleAddressChange('zip', e.target.value)}
                    className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] text-sm"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-[hsl(var(--ih-text-secondary))]">Clinical Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional instructions for lab..."
              className="bg-[hsl(var(--ih-bg-elevated))] border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text))] mt-1"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[hsl(var(--ih-border))] text-[hsl(var(--ih-text-secondary))]"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-purple-500 hover:bg-purple-600 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ordering...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Order Panel (${therapyPanel.price.toFixed(2)})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LabOrderModal
